import React, { useState, useMemo } from 'react';
import {
  Layout, Menu, Table, Avatar, Tag, Typography, Input, Select,
  Button, Space, Badge, Dropdown, Modal, Form, Divider
} from 'antd';
import {
  HomeOutlined, BarChartOutlined, FileTextOutlined,
  TrophyOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  BellOutlined, LogoutOutlined, SearchOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Header, Sider, Content } = Layout;
const { Title, Text }           = Typography;
const { Option }                = Select;

const INITIAL = [
  {
    id: 1,
    name: 'Әлихан Сәрсенбаев',
    email: 'alihan.sarsenbay@astcollege.edu.kz',
    survey: 'Рейтинг колледжей 2025',
    date: '15.05.2023, 14:32',
    status: 'pending',
    score: 88,
    answers: [
      { q: 'Оцените качество обучения',                 a: 'Отлично',         rate: '5/5' },
      { q: 'Порекомендуете ли колледж друзьям?',        a: 'Скорее да',       rate: '9/10' },
    ],
    pdf: '/demo-pdfs/alihan.pdf',
  },
  {
    id: 2,
    name: 'Айдана Тұрғынбаева',
    email: 'aidana.turgynbayeva@almcollege.edu.kz',
    survey: 'Рейтинг колледжей 2025',
    date: '14.05.2023, 10:15',
    status: 'approved',
    score: 93,
    answers: [
      { q: 'Как оцениваете работу персонала?',          a: 'Очень хорошо',    rate: '5/5' },
    ],
    pdf: '/demo-pdfs/aidana.pdf',
  },
  {
    id: 3,
    name: 'Нұрсұлтан Жақып',
    email: 'nursultan.zhakyp@shucollege.edu.kz',
    survey: 'Рейтинг колледжей 2025',
    date: '13.05.2023, 18:45',
    status: 'rejected',
    score: 64,
    answers: [],
    pdf: '/demo-pdfs/nursultan.pdf',
  },
  {
    id: 4,
    name: 'Динара Қуатова',
    email: 'dinara.kuatova@aktcollege.edu.kz',
    survey: 'Рейтинг колледжей 2025',
    date: '12.05.2023, 09:20',
    status: 'pending',
    score: 79,
    answers: [],
    pdf: '/demo-pdfs/dinara.pdf',
  },
];

const statusMeta = {
  pending:  { color: 'gold',  label: 'На проверке' },
  approved: { color: 'green', label: 'Одобрено'    },
  rejected: { color: 'red',   label: 'Отклонено'   },
};
const initials = full => full.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

export default function SurveyReviewPage() {
  const [rows,   setRows]   = useState(INITIAL);
  const [query,  setQuery]  = useState('');
  const [filter, setFilter] = useState('all');
  const [collapsed, setCol] = useState(false);

  const [openView, setOpenView] = useState(false);
  const [record,   setRecord]   = useState(null);
  const [form] = Form.useForm();

  /* фильтрация */
  const data = useMemo(
    () => rows.filter(r =>
        (filter === 'all' || r.status === filter) &&
        (r.name.toLowerCase().includes(query.toLowerCase()) ||
         r.email.toLowerCase().includes(query.toLowerCase()))
    ),
    [rows, filter, query]
  );

  const patchStatus = (id, st) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, status: st } : r));

  const confirmAction = (rec, st) => Modal.confirm({
    title: st === 'approved' ? 'Подтверждение одобрения' : 'Подтверждение отклонения',
    content: 'Вы уверены, что хотите ' +
             (st === 'approved' ? 'одобрить' : 'отклонить') +
             ' этот результат опроса?',
    okText: 'Подтвердить',
    cancelText: 'Отмена',
    okType: st === 'approved' ? 'primary' : 'danger',
    onOk: () => patchStatus(rec.id, st),
  });

  const columns = [
    {
      title: 'Участник',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Avatar style={{ background: '#d6d6ff', color: '#4B32C3' }}>
              {initials(r.name)}
            </Avatar>
            <Text>{r.name}</Text>
          </Space>
          <Text type="secondary" style={{ marginLeft: 40 }}>{r.email}</Text>
        </Space>
      ),
    },
    { title: 'Опрос',             dataIndex: 'survey' },
    { title: 'Дата завершения',   dataIndex: 'date'   },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: s => <Tag color={statusMeta[s].color}>{statusMeta[s].label}</Tag>,
    },
    { title: 'Результат', dataIndex: 'score', render: s => `${s}/100` },
    {
      title: 'Действия',
      render: (_, r) => (
        <Space>
          <Button type="link" size="small" onClick={() => { setRecord(r); setOpenView(true); }}>
            Просмотр
          </Button>
          {r.status !== 'approved' && (
            <Button type="link" size="small" style={{ color: 'green' }} onClick={() => confirmAction(r, 'approved')}>
              Одобрить
            </Button>
          )}
          {r.status !== 'rejected' && (
            <Button type="link" size="small" danger onClick={() => confirmAction(r, 'rejected')}>
              Отклонить
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const profileMenu = { items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Выйти' }] };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Сайдбар */}
      <Sider
        width={220}
        theme="light"
        collapsible
        collapsed={collapsed}
        onCollapse={setCol}
        trigger={null}
        style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
      >
        <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', paddingInline: 24 }}>
         {collapsed ? (
   <svg width="25" height="51" viewBox="0 0 45 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.77487 16.6822L0 12.1542V39.3224L7.77487 43.3738V34.3177L22.8534 42.4206V33.3645L7.77487 25.2617V16.6822Z" fill="#113F71"/>
<path d="M15.0785 20.257L8.48168 16.2056L22.8534 8.1028L15.7853 4.0514L22.8534 0L33.9267 6.0771L45 12.1542L37.9319 16.2056L30.1571 12.1542L15.0785 20.257Z" fill="#113F71"/>
<path d="M37.4607 16.6822L30.1571 20.257V38.8458L23.3246 42.4206V51L45 39.3224V34.9135V30.5047L41.466 32.4112L37.9319 34.3177L37.4607 16.6822Z" fill="#113F71"/>
</svg>

  ) : (
  <svg width="143" height="51" viewBox="0 0 223 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.77487 16.6822L0 12.1542V39.3224L7.77487 43.3738V34.3177L22.8534 42.4206V33.3645L7.77487 25.2617V16.6822Z" fill="#113F71"/>
<path d="M15.0785 20.257L8.48168 16.2056L22.8534 8.1028L15.7853 4.0514L22.8534 0L33.9267 6.0771L45 12.1542L37.9319 16.2056L30.1571 12.1542L15.0785 20.257Z" fill="#113F71"/>
<path d="M37.4607 16.6822L30.1571 20.257V38.8458L23.3246 42.4206V51L45 39.3224V34.9135V30.5047L41.466 32.4112L37.9319 34.3177L37.4607 16.6822Z" fill="#113F71"/>
<path d="M77.4734 43H72.0291V12.9583H62V8H87.5025V12.9583H77.4734V43Z" fill="#113F71"/>
<path d="M104.695 8L90.3679 43H96.9585L106.701 18.5L116.157 43H121.888L108.134 8H104.695Z" fill="#113F71"/>
<path d="M152.548 43H130.484V8H135.929V38.3333H152.548V43Z" fill="#113F71"/>
<path d="M173.466 8L159.425 43H165.443L175.185 18.5L185.214 43H190.945L177.191 8H173.466Z" fill="#113F71"/>
<path d="M196.963 8V43H202.407V34.25H211.576C213.869 34.25 215.535 33.571 217.021 32.5011C228.89 23.9525 220.97 8 211.576 8H196.963Z" fill="#113F71"/>
<path d="M202 29V14H210.91C220.808 16.2233 217.035 29 210.91 29H202Z" fill="white"/>
</svg>

  )}
        </div>

         <Menu
  mode="inline"
  defaultSelectedKeys={['dashboard']}
  items={[
    {
      key: 'dashboard',
      icon: <HomeOutlined />,
      label: <Link to="/dashboard">Кабинет</Link>,
    },
    {
      key: 'opros',
      icon: <FileTextOutlined />,
      label: <Link to="/opros">Опросы</Link>,
    },
    {
      key: 'manager',
      icon: <TrophyOutlined />,
      label: <Link to="/manager">Рейтинги (Проверка)</Link>,
    },
    {
      key: 'profile',
      icon: <BarChartOutlined />,
      label: <Link to="/profile">Профиль</Link>,
    },
  ]}
        />
      </Sider>

      <Layout>
        <Header style={{ background: '#fff', paddingInline: 24, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCol(!collapsed)} />
          <Title level={4} style={{ margin: 0 }}>Результаты опросов</Title>

          <Input
            prefix={<SearchOutlined />}
            placeholder="Поиск по имени или email..."
            allowClear
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ maxWidth: 240, marginLeft: 'auto' }}
          />

          <Select value={filter} style={{ width: 150 }} onChange={setFilter}>
            <Option value="all">Все статусы</Option>
            <Option value="pending">На проверке</Option>
            <Option value="approved">Одобрено</Option>
            <Option value="rejected">Отклонено</Option>
          </Select>

          <Space size="large">
            <Badge count={2}><BellOutlined style={{ fontSize: 18 }} /></Badge>
            <Dropdown menu={profileMenu} placement="bottomRight">
              <Avatar style={{ background: '#4B32C3', cursor: 'pointer' }}>AK</Avatar>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ background: '#F5F7FB', padding: '32px 48px' }}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={{ pageSize: 8 }}
            style={{ background: '#fff', borderRadius: 8 }}
          />
        </Content>
      </Layout>

      <Modal
        open={openView}
        width={900}
        title={record ? `Опрос: ${record.survey}` : ''}
        onCancel={() => setOpenView(false)}
        footer={null}
      >
        {record && (
          <>
            <Space align="center" size="large" style={{ marginBottom: 24 }}>
              <Avatar size={56} style={{ background: '#d6d6ff', color: '#4B32C3', fontSize: 20 }}>
                {initials(record.name)}
              </Avatar>
              <Space direction="vertical" size={0}>
                <Text strong>{record.name}</Text>
                <Text type="secondary">{record.email}</Text>
              </Space>
            </Space>

            <Space size="large" style={{ marginBottom: 32 }}>
              <Info label="Опрос"         value={record.survey} />
              <Info label="Дата завершения" value={record.date} />
              <Info label="Общий балл"     value={`${record.score}/100`} />
            </Space>

            <Title level={5}>Ответы</Title>
            {record.answers.length === 0 && <Text type="secondary">Ответы отсутствуют</Text>}
            {record.answers.map((a, i) => (
              <div key={i} style={{ background: '#F6F8FC', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <Text strong>{a.q}</Text><br />
                <Text>{a.a || '—'}</Text><br />
                {a.rate && <Tag color="green" style={{ marginTop: 4 }}>Оценка: {a.rate}</Tag>}
              </div>
            ))}

            <Divider />
            <Title level={5}>Прикреплённый PDF</Title>
            {record.pdf ? (
              <embed
                src={record.pdf}
                type="application/pdf"
                width="100%"
                height="400px"
                style={{ border: '1px solid #f0f0f0', borderRadius: 4 }}
              />
            ) : (
              <Text type="secondary">Файл отсутствует</Text>
            )}

            <Divider />
            <Form form={form} layout="vertical">
              <Form.Item name="comment" label="Комментарий менеджера">
                <Input.TextArea rows={3} placeholder="Введите комментарий…" />
              </Form.Item>
            </Form>

            <Divider />
            <Space style={{ float: 'right' }}>
              <Button danger onClick={() => { setOpenView(false); confirmAction(record, 'rejected'); }}>
                Отклонить
              </Button>
              <Button type="primary" onClick={() => { setOpenView(false); confirmAction(record, 'approved'); }}>
                Одобрить
              </Button>
            </Space>
          </>
        )}
      </Modal>
    </Layout>
  );
}

const Info = ({ label, value }) => (
  <div style={{ background: '#F6F8FC', padding: 16, borderRadius: 8, minWidth: 220 }}>
    <Text type="secondary">{label}</Text><br />
    <Text strong>{value}</Text>
  </div>
);
